import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { logProjectHistory } from '../../../utils/projectHistory';
import { syncJunctionTable } from '../../../utils/syncJunctionTable';
import { ENGINE_TYPE_LABELS, STATUS_LABELS } from '../../../utils/labels';
import { getBlockingInUseBooking } from '../constants';
import { appNow, appNowTimestamp, fromWallClockTimestamp, toWallClockTimestamp } from '../../../utils/timezone';
import type {
  Booking,
  BookingInput,
  BookingUpdate,
  Machine,
  MachineInput,
  MachineSpecificationValue,
  MachineUpdate,
  SpecificationKey,
} from '../types';

// `bookings` has three separate FKs to `subcontractor_users` (the booking's own contact,
// plus the pickup and hand-in confirmation contacts), so every embed needs an explicit
// `!fk_column` hint — otherwise PostgREST can't tell which relationship to follow.
const BOOKINGS_SELECT =
  '*, sites(id, name, color), subcontractor:subcontractors(id, name), subcontractor_user:subcontractor_users!subcontractor_user_id(id, first_name, last_name), pickup_subcontractor_user:subcontractor_users!pickup_subcontractor_user_id(id, first_name, last_name), handin_subcontractor_user:subcontractor_users!handin_subcontractor_user_id(id, first_name, last_name)' as const;

function describeMachineChanges(
  previous: Machine,
  updates: MachineUpdate,
  related?: { supplier?: Machine['supplier']; address?: Machine['address'] },
): string[] {
  const changes: string[] = [];
  if (updates.name !== undefined && updates.name !== previous.name) {
    changes.push(`naam van '${previous.name}' naar '${updates.name}'`);
  }
  if (updates.status !== undefined && updates.status !== previous.status) {
    changes.push(`status van ${STATUS_LABELS[previous.status]} naar ${STATUS_LABELS[updates.status]}`);
  }
  if (updates.engine_type !== undefined && updates.engine_type !== previous.engine_type) {
    changes.push(`motor van ${ENGINE_TYPE_LABELS[previous.engine_type]} naar ${ENGINE_TYPE_LABELS[updates.engine_type]}`);
  }
  if (updates.category !== undefined && updates.category !== previous.category) {
    changes.push(`categorie van '${previous.category ?? '-'}' naar '${updates.category ?? '-'}'`);
  }
  if (
    (updates.start_date !== undefined && updates.start_date !== previous.start_date) ||
    (updates.start_time !== undefined && updates.start_time !== previous.start_time)
  ) {
    changes.push('startdatum/tijd gewijzigd');
  }
  if (
    (updates.end_date !== undefined && updates.end_date !== previous.end_date) ||
    (updates.end_time !== undefined && updates.end_time !== previous.end_time)
  ) {
    changes.push('einddatum/tijd gewijzigd');
  }
  if (updates.supplier_id !== undefined && updates.supplier_id !== previous.supplier_id) {
    const oldName = previous.supplier?.name ?? '-';
    const newName = related?.supplier?.name ?? '-';
    changes.push(`leverancier van '${oldName}' naar '${newName}'`);
  }
  if (updates.notes !== undefined && updates.notes !== previous.notes) {
    changes.push('notities gewijzigd');
  }
  if (updates.inactive !== undefined && updates.inactive !== previous.inactive) {
    changes.push(updates.inactive ? 'gemarkeerd als inactief' : 'gemarkeerd als actief');
  }
  if (updates.option_ids !== undefined) {
    const previousIds = previous.option_ids ?? [];
    const added = updates.option_ids.filter((id) => !previousIds.includes(id));
    const removed = previousIds.filter((id) => !updates.option_ids!.includes(id));
    if (added.length > 0 || removed.length > 0) changes.push('opties aangepast');
  }
  if (updates.specifications !== undefined) {
    const previousMap = new Map((previous.specifications ?? []).map((s) => [s.key, s.value]));
    const newMap = new Map(updates.specifications.map((s) => [s.key, s.value]));
    const changed =
      previousMap.size !== newMap.size || [...newMap].some(([key, value]) => previousMap.get(key) !== value);
    if (changed) changes.push('specificaties aangepast');
  }
  return changes;
}

function describeBulkUpdate(updates: MachineUpdate): string {
  const parts: string[] = [];
  if (updates.status !== undefined) parts.push(`status naar ${STATUS_LABELS[updates.status]}`);
  if (updates.start_date !== undefined) parts.push(`startdatum naar ${updates.start_date}`);
  if (updates.end_date !== undefined) parts.push(`einddatum naar ${updates.end_date}`);
  return parts.length > 0 ? parts.join('; ') : 'gegevens bijgewerkt';
}

interface UseMachinesReturn {
  machines: Machine[];
  loading: boolean;
  error: string | null;
  addMachine: (machine: MachineInput) => Promise<void>;
  updateMachine: (machineId: string, updates: MachineUpdate, related?: { supplier?: Machine['supplier']; address?: Machine['address'] }) => Promise<void>;
  updateMachineStatus: (machineId: string, status: string) => Promise<void>;
  bulkUpdateMachines: (ids: string[], updates: MachineUpdate) => Promise<void>;
  deleteMachine: (machineId: string) => Promise<void>;
  reorderMachines: (orderedIds: string[]) => Promise<boolean>;
  addBooking: (machineId: string, booking: BookingInput) => Promise<void>;
  updateBooking: (bookingId: string, updates: BookingUpdate) => Promise<void>;
  confirmBookingPickup: (bookingId: string, contactUserId: string) => Promise<void>;
  confirmBookingHandin: (bookingId: string, contactUserId: string) => Promise<void>;
  deleteBooking: (bookingId: string) => Promise<void>;
  refetch: () => Promise<void>;
}

export function useMachines(projectId: string | null, orgId: string | null): UseMachinesReturn {
  const [machines, setMachines] = useState<Machine[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Counts in-flight mutations so Realtime events triggered by our own writes are suppressed.
  const pendingMutations = useRef(0);

  // Clear stale data immediately when the project changes so the user never sees
  // another project's machines while the new ones are loading.
  useEffect(() => {
    setMachines([]);
    setError(null);
  }, [projectId]);

  const loadMachines = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const [machinesRes, bookingsRes] = await Promise.all([
        supabase
          .from('machines')
          .select('*, category_ref:categories(id, name), supplier:suppliers(id, name, email), address:project_addresses(id, name, street, house_number, postal_code, city)')
          .eq('project_id', projectId)
          .order('created_at'),
        supabase
          .from('bookings')
          .select(BOOKINGS_SELECT)
          .eq('project_id', projectId),
      ]);

      const bookingsByMachineId: Record<string, Booking[]> = {};
      (bookingsRes.data ?? []).forEach((b: Booking) => {
        (bookingsByMachineId[b.machine_id] ??= []).push(b);
      });

      // machine_options is a many-to-many junction table (managed catalog links) — fetched
      // separately and merged client-side rather than nested in the machines select, same
      // reasoning as the bookings split: a nested one-to-many select triggers a LATERAL JOIN.
      type MachineOptionLink = { machine_id: string; option: { id: string; name: string } | null };
      const machineIds = (machinesRes.data ?? []).map((m: Machine) => m.id);
      let machineOptionRows: MachineOptionLink[] = [];
      if (machineIds.length > 0) {
        const { data } = await supabase
          .from('machine_options')
          .select('machine_id, option:options(id, name)')
          .in('machine_id', machineIds);
        machineOptionRows = (data ?? []) as unknown as MachineOptionLink[];
      }
      const linkedOptionsByMachineId: Record<string, { id: string; name: string }[]> = {};
      machineOptionRows.forEach((row) => {
        if (row.option) (linkedOptionsByMachineId[row.machine_id] ??= []).push(row.option);
      });

      // machine_specifications is likewise fetched separately — same "avoid LATERAL JOIN"
      // reasoning as machine_options above.
      type MachineSpecificationRow = { machine_id: string; specification_key: SpecificationKey; value: number };
      let machineSpecRows: MachineSpecificationRow[] = [];
      if (machineIds.length > 0) {
        const { data } = await supabase
          .from('machine_specifications')
          .select('machine_id, specification_key, value')
          .in('machine_id', machineIds);
        machineSpecRows = (data ?? []) as MachineSpecificationRow[];
      }
      const specificationsByMachineId: Record<string, MachineSpecificationValue[]> = {};
      machineSpecRows.forEach((row) => {
        (specificationsByMachineId[row.machine_id] ??= []).push({ key: row.specification_key, value: row.value });
      });

      const sorted = (machinesRes.data ?? [])
        .map((m: Machine) => {
          const linked = linkedOptionsByMachineId[m.id] ?? [];
          return {
            ...m,
            category: m.category_ref?.name ?? m.category,
            option_ids: linked.map((o) => o.id),
            options: [...linked.map((o) => o.name), ...(m.options ?? [])],
            specifications: specificationsByMachineId[m.id] ?? [],
            bookings: bookingsByMachineId[m.id] ?? [],
          };
        })
        .sort((a: Machine, b: Machine) => {
          const ai = a.sort_order ?? Infinity;
          const bi = b.sort_order ?? Infinity;
          if (ai !== bi) return ai - bi;
          return a.name.localeCompare(b.name, 'nl');
        });
      setMachines(sorted);
    } catch {
      // Keep existing machines in state — losing all data on a transient failure is worse
      // than showing slightly stale data. The error banner tells the user something went wrong.
      setError('Er ging iets mis bij het laden van de planning. Probeer het opnieuw.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  // Lightweight refresh: only re-fetches bookings and merges into existing machine state.
  // Used after booking add/update to get nested site and subcontractor names without
  // re-fetching all machines. On failure, the optimistic state is kept — Realtime will
  // sync when the connection is restored.
  const refreshBookings = useCallback(async () => {
    if (!projectId) return;
    try {
      const { data } = await supabase
        .from('bookings')
        .select(BOOKINGS_SELECT)
        .eq('project_id', projectId);

      const bookingsByMachineId: Record<string, Booking[]> = {};
      (data ?? []).forEach((b: Booking) => {
        (bookingsByMachineId[b.machine_id] ??= []).push(b);
      });

      setMachines((prev) => prev.map((m) => ({ ...m, bookings: bookingsByMachineId[m.id] ?? [] })));
    } catch {
      // Keep optimistic state — Realtime will trigger a full sync when connection returns.
    }
  }, [projectId]);

  useEffect(() => {
    loadMachines();
  }, [loadMachines]);

  useEffect(() => {
    if (!projectId) return;
    const channel = supabase
      .channel(`planning-${projectId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'machines', filter: `project_id=eq.${projectId}` }, () => {
        if (pendingMutations.current > 0) return;
        loadMachines();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'bookings', filter: `project_id=eq.${projectId}` }, () => {
        if (pendingMutations.current > 0) return;
        loadMachines();
      })
      .subscribe((status, err) => {
        // Realtime fails silently otherwise: a channel that never delivers
        // events (e.g. the table missing from the `supabase_realtime`
        // publication) still resolves 'SUBSCRIBED' with no error, so the only
        // way to spot a broken subscription is watching for the non-OK statuses.
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          console.error(`[useMachines] Realtime channel planning-${projectId} status: ${status}`, err);
        }
      });
    return () => { supabase.removeChannel(channel); };
  }, [projectId, loadMachines]);

  const addMachine = async (machine: MachineInput): Promise<void> => {
    const { option_ids: optionIds, specifications, ...machineFields } = machine;
    const maxOrder = machines.reduce((max, m) => Math.max(max, m.sort_order ?? 0), 0);
    pendingMutations.current++;
    try {
      const { data, error: err } = await supabase
        .from('machines')
        .insert({ ...machineFields, project_id: projectId, sort_order: maxOrder + 1 })
        .select('id')
        .single();
      if (err) throw err;

      // The machines row already exists at this point — always refresh so it becomes visible
      // even if a follow-up junction insert below fails, instead of only rethrowing and leaving
      // a real DB row invisible in local state until the next reload/Realtime sync.
      let linkError: unknown = null;
      try {
        if (optionIds && optionIds.length > 0 && orgId) {
          const { error: optErr } = await supabase
            .from('machine_options')
            .insert(optionIds.map((option_id) => ({ machine_id: data.id, option_id, organization_id: orgId })));
          if (optErr) throw optErr;
        }
        if (specifications && specifications.length > 0 && orgId) {
          const { error: specErr } = await supabase
            .from('machine_specifications')
            .insert(specifications.map((s) => ({ machine_id: data.id, specification_key: s.key, value: s.value, organization_id: orgId })));
          if (specErr) throw specErr;
        }
      } catch (e) {
        linkError = e;
      }

      await loadMachines();
      if (linkError) throw linkError;

      if (projectId && data) {
        await logProjectHistory({
          projectId,
          organizationId: orgId,
          action: 'create',
          entityType: 'machine',
          entityName: machineFields.name,
          description: `Machine '${machineFields.name}' toegevoegd`,
        });
      }
    } finally {
      pendingMutations.current--;
    }
  };

  const reorderMachines = async (orderedIds: string[]): Promise<boolean> => {
    const snapshot = machines;
    setMachines((prev) => {
      const byId = Object.fromEntries(prev.map((m) => [m.id, m]));
      return orderedIds.map((id, i) => ({ ...byId[id], sort_order: i + 1 }));
    });
    // Keep pendingMutations elevated until after loadMachines so that Realtime
    // events (one per machine update) don't trigger concurrent loadMachines calls
    // while sort_order values are still being committed.
    pendingMutations.current++;
    try {
      const results = await Promise.all(
        orderedIds.map((id, index) =>
          supabase.from('machines').update({ sort_order: index + 1 }).eq('id', id)
        )
      );
      const firstError = results.find((r) => r.error)?.error;
      if (firstError) throw firstError;
      await loadMachines();
      if (projectId) {
        await logProjectHistory({
          projectId,
          organizationId: orgId,
          action: 'update',
          entityType: 'machine',
          entityName: `${orderedIds.length} machines`,
          description: 'Machines herschikt',
        });
      }
      return true;
    } catch {
      setMachines(snapshot);
      return false;
    } finally {
      pendingMutations.current--;
    }
  };

  const updateMachine = async (machineId: string, updates: MachineUpdate, related?: { supplier?: Machine['supplier']; address?: Machine['address'] }): Promise<void> => {
    const snapshot = machines;
    const previous = snapshot.find((m) => m.id === machineId);
    const { option_ids: newOptionIds, specifications: newSpecifications, ...machineUpdates } = updates;
    setMachines((prev) => prev.map((m) => (m.id === machineId ? { ...m, ...updates, ...(related ?? {}) } : m)));
    pendingMutations.current++;
    try {
      if (Object.keys(machineUpdates).length > 0) {
        const { error: err } = await supabase.from('machines').update(machineUpdates).eq('id', machineId);
        if (err) throw err;
      }
      if (newOptionIds !== undefined) {
        await syncJunctionTable({
          table: 'machine_options',
          fixedColumn: 'machine_id',
          fixedValue: machineId,
          varyingColumn: 'option_id',
          previousIds: snapshot.find((m) => m.id === machineId)?.option_ids ?? [],
          nextIds: newOptionIds,
          organizationId: orgId,
        });
      }
      if (newSpecifications !== undefined) {
        // Delete+reinsert rather than diffing by key: unlike machine_options (plain presence),
        // a specification row carries a value that can change while the key stays the same, and
        // this set is always submitted in full from the edit form (max 12 rows) — same reasoning
        // as category_specifications in useManageCategories.
        const { error: delSpecErr } = await supabase.from('machine_specifications').delete().eq('machine_id', machineId);
        if (delSpecErr) throw delSpecErr;
        if (newSpecifications.length > 0 && orgId) {
          const { error: insSpecErr } = await supabase
            .from('machine_specifications')
            .insert(newSpecifications.map((s) => ({ machine_id: machineId, specification_key: s.key, value: s.value, organization_id: orgId })));
          if (insSpecErr) throw insSpecErr;
        }
      }
      if (projectId && previous) {
        const changes = describeMachineChanges(previous, updates, related);
        if (changes.length > 0) {
          await logProjectHistory({
            projectId,
            organizationId: orgId,
            action: 'update',
            entityType: 'machine',
            entityName: updates.name ?? previous.name,
            description: `Machine '${updates.name ?? previous.name}' bijgewerkt: ${changes.join('; ')}`,
          });
        }
      }
    } catch (e) {
      setMachines(snapshot);
      throw e;
    } finally {
      pendingMutations.current--;
    }
  };

  const updateMachineStatus = async (machineId: string, status: string): Promise<void> => {
    const snapshot = machines;
    const previous = snapshot.find((m) => m.id === machineId);
    setMachines((prev) => prev.map((m) => (m.id === machineId ? { ...m, status: status as Machine['status'] } : m)));
    pendingMutations.current++;
    try {
      const { error: err } = await supabase.from('machines').update({ status }).eq('id', machineId);
      if (err) throw err;
      if (projectId && previous && previous.status !== status) {
        await logProjectHistory({
          projectId,
          organizationId: orgId,
          action: 'update',
          entityType: 'machine',
          entityName: previous.name,
          description: `Machine '${previous.name}' bijgewerkt: status van ${STATUS_LABELS[previous.status]} naar ${STATUS_LABELS[status as Machine['status']]}`,
        });
      }
    } catch (e) {
      setMachines(snapshot);
      throw e;
    } finally {
      pendingMutations.current--;
    }
  };

  const bulkUpdateMachines = async (ids: string[], updates: MachineUpdate): Promise<void> => {
    const snapshot = machines;
    setMachines((prev) => prev.map((m) => ids.includes(m.id) ? { ...m, ...updates } : m));
    pendingMutations.current++;
    try {
      const results = await Promise.all(
        ids.map((id) => supabase.from('machines').update(updates).eq('id', id))
      );
      const firstError = results.find((r) => r.error)?.error;
      if (firstError) throw firstError;
      if (projectId) {
        await logProjectHistory({
          projectId,
          organizationId: orgId,
          action: 'update',
          entityType: 'machine',
          entityName: `${ids.length} machines`,
          description: `${ids.length} machines bijgewerkt: ${describeBulkUpdate(updates)}`,
        });
      }
    } catch (e) {
      setMachines(snapshot);
      throw e;
    } finally {
      pendingMutations.current--;
    }
  };

  const deleteMachine = async (machineId: string): Promise<void> => {
    const snapshot = machines;
    const previous = snapshot.find((m) => m.id === machineId);
    setMachines((prev) => prev.filter((m) => m.id !== machineId));
    pendingMutations.current++;
    try {
      const { error: err } = await supabase.from('machines').delete().eq('id', machineId);
      if (err) throw err;
      if (projectId && previous) {
        await logProjectHistory({
          projectId,
          organizationId: orgId,
          action: 'delete',
          entityType: 'machine',
          entityName: previous.name,
          description: `Machine '${previous.name}' verwijderd`,
        });
      }
    } catch (e) {
      setMachines(snapshot);
      throw e;
    } finally {
      pendingMutations.current--;
    }
  };

  const addBooking = async (machineId: string, booking: BookingInput): Promise<void> => {
    const snapshot = machines;
    const tempId = crypto.randomUUID();
    const optimistic: Booking = { ...booking, id: tempId, machine_id: machineId, project_id: projectId! };
    setMachines((prev) => prev.map((m) =>
      m.id === machineId ? { ...m, bookings: [...(m.bookings ?? []), optimistic] } : m
    ));
    pendingMutations.current++;
    try {
      const { error: err } = await supabase
        .from('bookings')
        .insert({ ...booking, machine_id: machineId, project_id: projectId });
      if (err) throw err;
      await refreshBookings();
      const machineName = snapshot.find((m) => m.id === machineId)?.name ?? machineId;
      if (projectId) {
        await logProjectHistory({
          projectId,
          organizationId: orgId,
          action: 'create',
          entityType: 'booking',
          entityName: machineName,
          description: `Boeking toegevoegd voor machine '${machineName}'`,
        });
      }
    } catch (e) {
      setMachines(snapshot);
      throw e;
    } finally {
      pendingMutations.current--;
    }
  };

  const updateBooking = async (bookingId: string, updates: BookingUpdate): Promise<void> => {
    const snapshot = machines;
    setMachines((prev) => prev.map((m) => ({
      ...m,
      bookings: (m.bookings ?? []).map((b) => (b.id === bookingId ? { ...b, ...updates } : b)),
    })));
    pendingMutations.current++;
    try {
      const { error: err } = await supabase.from('bookings').update(updates).eq('id', bookingId);
      if (err) throw err;
      await refreshBookings();
      const machineName = snapshot.find((m) => (m.bookings ?? []).some((b) => b.id === bookingId))?.name;
      if (projectId && machineName) {
        await logProjectHistory({
          projectId,
          organizationId: orgId,
          action: 'update',
          entityType: 'booking',
          entityName: machineName,
          description: `Boeking bijgewerkt voor machine '${machineName}'`,
        });
      }
    } catch (e) {
      setMachines(snapshot);
      throw e;
    } finally {
      pendingMutations.current--;
    }
  };

  const confirmBookingPickup = async (bookingId: string, contactUserId: string): Promise<void> => {
    const snapshot = machines;
    // Defense in depth: never let a pickup go through while another booking on the same
    // machine is still physically out (picked up, not yet handed in), even if a caller's own
    // UI-level check was somehow bypassed or is out of date.
    const machineForBooking = snapshot.find((m) => (m.bookings ?? []).some((b) => b.id === bookingId));
    const bookingToConfirm = machineForBooking?.bookings?.find((b) => b.id === bookingId);
    if (bookingToConfirm && getBlockingInUseBooking(bookingToConfirm, machineForBooking?.bookings ?? [])) {
      throw new Error('Ophaling niet mogelijk: een andere boeking op deze machine is nog niet teruggebracht.');
    }
    const confirmedAt = new Date().toISOString();
    const now = appNow();
    const nowMs = appNowTimestamp();
    // The booking's start is always pulled in to the actual pickup moment — a subcontractor
    // rarely shows up at exactly the planned time, and the confirmation timestamp is the only
    // reliable record of when the machine actually left. When the booking's own planned end
    // had already passed too, its whole window is shifted forward instead of just the start —
    // otherwise end_date/end_time would be left before the new start, an inverted range the
    // planning grid can't render — preserving the original duration rather than truncating it.
    const originalStart = toWallClockTimestamp(bookingToConfirm?.start_date, bookingToConfirm?.start_time);
    const originalEnd = toWallClockTimestamp(bookingToConfirm?.end_date, bookingToConfirm?.end_time);
    const endAlreadyElapsed = originalEnd !== undefined && originalEnd < nowMs;
    const dateOverride: Partial<Booking> = { start_date: now.format('YYYY-MM-DD'), start_time: now.format('HH:mm') };
    if (endAlreadyElapsed) {
      const duration = originalStart !== undefined ? originalEnd - originalStart : 0;
      const newEnd = fromWallClockTimestamp(nowMs + duration);
      dateOverride.end_date = newEnd.date;
      dateOverride.end_time = newEnd.time;
    }
    setMachines((prev) => prev.map((m) => ({
      ...m,
      bookings: (m.bookings ?? []).map((b) =>
        b.id === bookingId
          ? { ...b, pickup_confirmed_at: confirmedAt, pickup_subcontractor_user_id: contactUserId, ...dateOverride }
          : b
      ),
    })));
    pendingMutations.current++;
    try {
      const { error: err } = await supabase
        .from('bookings')
        .update({ pickup_confirmed_at: confirmedAt, pickup_subcontractor_user_id: contactUserId, ...dateOverride })
        .eq('id', bookingId);
      if (err) throw err;
      await refreshBookings();
      const machineName = snapshot.find((m) => (m.bookings ?? []).some((b) => b.id === bookingId))?.name;
      if (projectId && machineName) {
        await logProjectHistory({
          projectId,
          organizationId: orgId,
          action: 'pickup_confirmed',
          entityType: 'booking',
          entityName: machineName,
          description: `Ophaling bevestigd voor boeking op machine '${machineName}'${endAlreadyElapsed ? ' (volledige periode verschoven naar ophaalmoment)' : ' (startdatum aangepast naar ophaalmoment)'}`,
        });
      }
    } catch (e) {
      setMachines(snapshot);
      throw e;
    } finally {
      pendingMutations.current--;
    }
  };

  const confirmBookingHandin = async (bookingId: string, contactUserId: string): Promise<void> => {
    const snapshot = machines;
    const confirmedAt = new Date().toISOString();
    const now = appNow();
    // The booking's end is always pulled in to the actual hand-in moment, whether the
    // machine came back early or late — the confirmation timestamp is the actual return time.
    const endOverride = { end_date: now.format('YYYY-MM-DD'), end_time: now.format('HH:mm') };
    setMachines((prev) => prev.map((m) => ({
      ...m,
      bookings: (m.bookings ?? []).map((b) =>
        b.id === bookingId
          ? { ...b, handin_confirmed_at: confirmedAt, handin_subcontractor_user_id: contactUserId, ...endOverride }
          : b
      ),
    })));
    pendingMutations.current++;
    try {
      const { error: err } = await supabase
        .from('bookings')
        .update({ handin_confirmed_at: confirmedAt, handin_subcontractor_user_id: contactUserId, ...endOverride })
        .eq('id', bookingId);
      if (err) throw err;
      await refreshBookings();
      const machineName = snapshot.find((m) => (m.bookings ?? []).some((b) => b.id === bookingId))?.name;
      if (projectId && machineName) {
        await logProjectHistory({
          projectId,
          organizationId: orgId,
          action: 'handin_confirmed',
          entityType: 'booking',
          entityName: machineName,
          description: `Teruggave bevestigd voor boeking op machine '${machineName}' (einddatum aangepast naar teruggavemoment)`,
        });
      }
    } catch (e) {
      setMachines(snapshot);
      throw e;
    } finally {
      pendingMutations.current--;
    }
  };

  const deleteBooking = async (bookingId: string): Promise<void> => {
    const snapshot = machines;
    const machineName = snapshot.find((m) => (m.bookings ?? []).some((b) => b.id === bookingId))?.name;
    setMachines((prev) => prev.map((m) => ({
      ...m,
      bookings: (m.bookings ?? []).filter((b) => b.id !== bookingId),
    })));
    pendingMutations.current++;
    try {
      const { error: err } = await supabase.from('bookings').delete().eq('id', bookingId);
      if (err) throw err;
      if (projectId && machineName) {
        await logProjectHistory({
          projectId,
          organizationId: orgId,
          action: 'delete',
          entityType: 'booking',
          entityName: machineName,
          description: `Boeking verwijderd voor machine '${machineName}'`,
        });
      }
    } catch (e) {
      setMachines(snapshot);
      throw e;
    } finally {
      pendingMutations.current--;
    }
  };

  return {
    machines,
    loading,
    error,
    addMachine,
    updateMachine,
    updateMachineStatus,
    bulkUpdateMachines,
    deleteMachine,
    reorderMachines,
    addBooking,
    updateBooking,
    confirmBookingPickup,
    confirmBookingHandin,
    deleteBooking,
    refetch: loadMachines,
  };
}

export type { Booking, BookingInput, BookingUpdate, Machine, MachineInput, MachineUpdate };
